import { ChevronRight, MessageSquarePlus, Search, Sparkles } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '@/api/client';
import { useIntegrations, useMyRequests } from '@/api/hooks';
import type { Integration } from '@/api/types';
import { Badge, Button, Card, EmptyState, ErrorNote, IntegrationIcon, LiveRegion, PageHeader, Skeleton } from '@/components/ui';
import { inputClass } from '@/components/styles';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { CustomDashboardDialog, RequestIntegrationDialog } from './RequestDialogs';
import { StatusBadge } from './status';

function matches(i: Integration, q: string) {
  if (!q) return true;
  const hay = [i.name, i.category, i.account_label, ...i.kpis.map((k) => k.label)].join(' ').toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((w) => hay.includes(w));
}

export function IntegrationCard({ integration: i }: { integration: Integration }) {
  return (
    <Card as="li" className="group relative flex flex-col p-5 transition-colors hover:border-ink/30">
      <div className="flex items-start gap-3">
        <IntegrationIcon name={i.name} size="lg" />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">
            <Link to={`/integrations/${i.id}`} className="after:absolute after:inset-0 after:rounded-card">
              {i.name}
            </Link>
          </h3>
          <p className="text-xs text-muted-ink">{i.account_label}</p>
        </div>
        <ChevronRight className="size-4 text-muted-ink transition-transform group-hover:translate-x-0.5" aria-hidden />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <StatusBadge status={i.status} />
        {i.connected_count > 0 && <Badge tone="ink">Connected{i.connected_count > 1 ? ` ×${i.connected_count}` : ''}</Badge>}
      </div>
      <p className="mt-3 line-clamp-2 text-sm text-muted-ink">{i.user_requirements}</p>
      {i.kpis.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1" aria-label={`${i.name} KPIs`}>
          {i.kpis.slice(0, 5).map((k) => (
            <li key={k.key} className="rounded-full bg-muted px-2 py-0.5 text-xs text-ink">
              {k.label}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function Library() {
  const q = useIntegrations();
  const requests = useMyRequests();
  const [search, setSearch] = useState('');
  const deferred = useDeferredValue(search.trim());
  const [category, setCategory] = useState<string>('all');
  const [requestOpen, setRequestOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const groups = useMemo(() => {
    if (!q.data) return [];
    return q.data.categories
      .filter((c) => category === 'all' || c.id === category)
      .map((c) => ({ category: c, items: q.data.integrations.filter((i) => i.category === c.id && matches(i, deferred)) }))
      .filter((g) => g.items.length > 0);
  }, [q.data, category, deferred]);
  const count = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div>
      <PageHeader
        title="Integrations"
        description="Connect the apps you already use. Each one brings its 3–5 headline numbers."
        actions={
          <Button icon={<MessageSquarePlus className="size-4" aria-hidden />} onClick={() => setRequestOpen(true)}>
            Request an Integration
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-3">
        <div className="relative max-w-md">
          <label htmlFor="integration-search" className="sr-only">
            Search integrations
          </label>
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-ink" aria-hidden />
          <input
            id="integration-search"
            type="search"
            placeholder="Search apps or numbers, e.g. “revenue”"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cx(inputClass, 'pl-9')}
          />
        </div>
        {q.data && (
          <div role="group" aria-label="Category" className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
            {[{ id: 'all', name: 'All' }, ...q.data.categories].map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={category === c.id}
                onClick={() => setCategory(c.id)}
                className={cx(
                  'h-8 shrink-0 rounded-full border px-3 text-sm transition-colors',
                  category === c.id ? 'border-ink bg-ink text-paper' : 'border-border bg-card text-ink hover:bg-muted',
                )}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <LiveRegion message={q.data ? `${count} integration${count === 1 ? '' : 's'} shown` : ''} />

      {q.isError && <ErrorNote message={errorMessage(q.error)} onRetry={() => void q.refetch()} />}
      {q.isPending && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      )}
      {q.data && count === 0 && (
        <EmptyState
          icon={<Search className="size-5" aria-hidden />}
          title={`No apps match “${deferred}”`}
          actions={
            <Button variant="primary" onClick={() => setRequestOpen(true)}>
              Request {deferred ? `“${deferred}”` : 'an Integration'}
            </Button>
          }
        >
          Tell us which app you use and we’ll count your vote.
        </EmptyState>
      )}

      <div className="flex flex-col gap-10">
        {groups.map(({ category: c, items }) => (
          <section key={c.id} aria-labelledby={`cat-${c.id}`}>
            <h2 id={`cat-${c.id}`} className="mb-3 text-sm font-semibold tracking-wide text-muted-ink uppercase">
              {c.name}
            </h2>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((i) => (
                <IntegrationCard key={i.id} integration={i} />
              ))}
            </ul>
          </section>
        ))}
      </div>

      <Card className="mt-12 flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-yellow text-yellow-ink">
            <Sparkles className="size-5" aria-hidden />
          </span>
          <div>
            <h2 className="font-semibold">Looking for something more specific?</h2>
            <p className="text-sm text-muted-ink">We build custom dashboards for numbers and layouts MyDash doesn’t cover yet.</p>
          </div>
        </div>
        <Button variant="primary" onClick={() => setCustomOpen(true)}>
          Request a Custom Dashboard
        </Button>
      </Card>

      {requests.data && requests.data.length > 0 && (
        <section aria-labelledby="my-requests" className="mt-10">
          <h2 id="my-requests" className="mb-3 text-base font-semibold">
            Your requests
          </h2>
          <Card as="div" className="divide-y divide-border">
            {requests.data.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.kind === 'integration' ? r.name : 'Custom dashboard'}</p>
                  <p className="truncate text-xs text-muted-ink">
                    {r.kind === 'custom_dashboard' && r.description ? `${r.description} · ` : ''}
                    {formatDateTime(r.created_at)}
                  </p>
                </div>
                <Badge tone={r.status === 'done' ? 'good' : r.status === 'declined' ? 'bad' : r.status === 'planned' ? 'ink' : 'neutral'}>
                  {r.status === 'new' ? 'Received' : r.status[0]!.toUpperCase() + r.status.slice(1)}
                </Badge>
              </div>
            ))}
          </Card>
        </section>
      )}

      <RequestIntegrationDialog open={requestOpen} onClose={() => setRequestOpen(false)} />
      <CustomDashboardDialog open={customOpen} onClose={() => setCustomOpen(false)} />
    </div>
  );
}
